import React, { useState, useRef } from 'react';
import { Popover, Button, Box, Typography, Divider, useTheme, List, ListItemButton, ListItemText } from '@mui/material';
import ViewListIcon from '@mui/icons-material/ViewList';
import { SharedView, ViewTemplate } from '../hooks/useSharedViews';

interface SavedView {
  name: string;
  [key: string]: any;
}

interface ViewSelectorProps {
  selectedViewName: string;
  savedViews: SavedView[];
  sharedViews: SharedView[];
  viewTemplates: ViewTemplate[];
  onViewChange: (name: string) => void;
  onApplySharedView?: (view: SharedView | ViewTemplate, type: 'shared' | 'template') => void;
  label?: string;
}

export const ViewSelector: React.FC<ViewSelectorProps> = ({
  selectedViewName,
  savedViews,
  sharedViews,
  viewTemplates,
  onViewChange,
  onApplySharedView,
  label = 'View'
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = () => {
    setAnchorEl(buttonRef.current);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getDisplayValue = () => {
    if (!selectedViewName) {
      return '(select view)';
    }

    // Check if it's a template
    if (selectedViewName.startsWith('template:')) {
      const templateId = selectedViewName.replace('template:', '');
      const template = viewTemplates.find(t => t.id === templateId);
      return template ? template.name : selectedViewName;
    }

    // Check if it's a shared view
    if (selectedViewName.startsWith('shared:')) {
      const sharedId = selectedViewName.replace('shared:', '');
      const shared = sharedViews.find(s => s.id === sharedId);
      return shared ? shared.name : selectedViewName;
    }

    // Personal view
    return selectedViewName;
  };

  const handleSelectView = (value: string) => {
    if (value.startsWith('template:')) {
      const templateId = value.replace('template:', '');
      const template = viewTemplates.find(t => t.id === templateId);
      if (template && onApplySharedView) {
        onApplySharedView(template, 'template');
      }
    } else if (value.startsWith('shared:')) {
      const sharedId = value.replace('shared:', '');
      const shared = sharedViews.find(s => s.id === sharedId);
      if (shared && onApplySharedView) {
        onApplySharedView(shared, 'shared');
      }
    } else {
      onViewChange(value);
    }
    handleClose();
  };

  return (
    <>
      <Button
        ref={buttonRef}
        onClick={handleClick}
        variant="outlined"
        size="small"
        startIcon={<ViewListIcon sx={{ fontSize: 16 }} />}
        sx={{
          minWidth: 160,
          maxWidth: 300,
          height: 36,
          justifyContent: 'flex-start',
          textTransform: 'none',
          borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
          color: selectedViewName ? 'text.primary' : 'text.secondary',
          '&:hover': {
            borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
            backgroundColor: 'action.hover'
          },
          px: 1.5,
          display: 'flex',
          gap: 1
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'flex-start',
          flex: 1,
          overflow: 'hidden'
        }}>
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: '0.65rem', 
              lineHeight: 1,
              color: 'text.secondary',
              mb: 0.25
            }}
          >
            {label}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              fontSize: '0.875rem',
              fontWeight: 500,
              color: selectedViewName ? 'text.primary' : 'text.secondary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              width: '100%',
              textAlign: 'left'
            }}
          >
            {getDisplayValue()}
          </Typography>
        </Box>
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              boxShadow: theme.shadows[8],
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              overflow: 'hidden',
              backgroundColor: theme.palette.background.paper,
              minWidth: 300,
              maxWidth: 500,
              maxHeight: 500
            }
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.secondary' }}>
            Select a View
          </Typography>

          <List sx={{ p: 0, maxHeight: 400, overflowY: 'auto' }}>
            {/* Default option */}
            <ListItemButton
              selected={!selectedViewName}
              onClick={() => handleSelectView('')}
              sx={{ 
                borderRadius: 1,
                mb: 0.5,
                '&.Mui-selected': {
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(144, 202, 249, 0.16)' 
                    : 'rgba(25, 118, 210, 0.08)'
                }
              }}
            >
              <ListItemText 
                primary="(select view)" 
                primaryTypographyProps={{ 
                  fontSize: '0.875rem',
                  color: 'text.secondary',
                  fontStyle: 'italic'
                }} 
              />
            </ListItemButton>

            {/* View Templates */}
            {viewTemplates.length > 0 && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" sx={{ px: 2, py: 1, fontWeight: 600, color: 'text.secondary', display: 'block' }}>
                  Templates
                </Typography>
                {viewTemplates.map((template) => (
                  <ListItemButton
                    key={template.id}
                    selected={selectedViewName === `template:${template.id}`}
                    onClick={() => handleSelectView(`template:${template.id}`)}
                    sx={{ 
                      borderRadius: 1,
                      mb: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: theme.palette.mode === 'dark' 
                          ? 'rgba(144, 202, 249, 0.16)' 
                          : 'rgba(25, 118, 210, 0.08)'
                      }
                    }}
                  >
                    <ListItemText 
                      primary={template.name}
                      secondary={`${template.usageCount} uses${template.isDefault ? ' • Default' : ''}`}
                      primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: template.isDefault ? 600 : 400 }}
                      secondaryTypographyProps={{ fontSize: '0.75rem' }}
                    />
                  </ListItemButton>
                ))}
              </>
            )}

            {/* Shared Views */}
            {sharedViews.length > 0 && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" sx={{ px: 2, py: 1, fontWeight: 600, color: 'text.secondary', display: 'block' }}>
                  Shared Views
                </Typography>
                {sharedViews.map((view) => (
                  <ListItemButton
                    key={view.id}
                    selected={selectedViewName === `shared:${view.id}`}
                    onClick={() => handleSelectView(`shared:${view.id}`)}
                    sx={{ 
                      borderRadius: 1,
                      mb: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: theme.palette.mode === 'dark' 
                          ? 'rgba(144, 202, 249, 0.16)' 
                          : 'rgba(25, 118, 210, 0.08)'
                      }
                    }}
                  >
                    <ListItemText 
                      primary={`🌍 ${view.name}`}
                      secondary={`by ${view.createdByEmail || 'Unknown'} • ${view.usageCount} uses`}
                      primaryTypographyProps={{ fontSize: '0.875rem' }}
                      secondaryTypographyProps={{ fontSize: '0.75rem' }}
                    />
                  </ListItemButton>
                ))}
              </>
            )}

            {/* Personal Views */}
            {savedViews.length > 0 && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" sx={{ px: 2, py: 1, fontWeight: 600, color: 'text.secondary', display: 'block' }}>
                  👤 My Views
                </Typography>
                {savedViews.map((view, index) => (
                  <ListItemButton
                    key={`${view.name}-${index}`}
                    selected={selectedViewName === view.name}
                    onClick={() => handleSelectView(view.name)}
                    sx={{ 
                      borderRadius: 1,
                      mb: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: theme.palette.mode === 'dark' 
                          ? 'rgba(144, 202, 249, 0.16)' 
                          : 'rgba(25, 118, 210, 0.08)'
                      }
                    }}
                  >
                    <ListItemText 
                      primary={view.name}
                      primaryTypographyProps={{ fontSize: '0.875rem' }}
                    />
                  </ListItemButton>
                ))}
              </>
            )}
          </List>
        </Box>
      </Popover>
    </>
  );
};

